import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useDealbreakerQuestions } from "@/hooks/useDealbreakerQuestions";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Loader2, HeartHandshake, ChevronLeft, ChevronRight, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { PriorityBadges } from "./PriorityBadges";
import { getImportanceLabel } from "@/utils/weightedCompatibility";
import { cn } from "@/lib/utils";
import { useSignedPhotoUrl, useSignedPhotoUrls } from "@/lib/photoUrl";

interface Profile {
  id: string;
  first_name: string;
  age: number;
  gender: string;
  location: string;
  education_level: string;
  religiosity_level: string;
  prayer_frequency: string;
  bio?: string;
  profile_picture_url?: string;
  profile_pictures?: string[] | null;
  is_active: boolean;
  profile_completed?: boolean;
  marital_status: string;
  ethnicity: string;
  citizenship_status: string;
  height_feet?: number;
  height_inches?: number;
  sect: string;
  masjid_attendance: string;
  occupation?: string;
  field_of_study?: string;
  employment_status?: string;
  career_description?: string;
  financial_status: number;
  personality_type: number;
  personality_description?: string;
  importance_of_islam: number;
  quran_knowledge: string;
  islamic_knowledge_level: string;
  smoking: string;
  dietary_preferences?: string[] | null;
  exercise_routine?: string;
  has_children?: boolean;
  children_count?: number;
  has_beard?: string;
  wears_hijab?: boolean;
  wears_hijab_level?: string;
  willing_to_marry_divorced?: boolean;
  willing_to_marry_with_children?: boolean;
  willing_to_marry_convert?: boolean;
  willing_to_relocate?: boolean;
  seeking_age_min: number;
  seeking_age_max: number;
  seeking_location_preference?: string;
  seeking_description?: string;
  seeking_education?: string[] | null;
  seeking_ethnicity?: string[] | null;
  seeking_citizenship?: string[] | null;
  seeking_sect?: string[] | null;
  seeking_religiosity?: string[] | null;
  seeking_marital_status?: string[] | null;
  seeking_personality_min?: number;
  seeking_personality_max?: number;
  seeking_height_min_feet?: number;
  seeking_height_min_inches?: number;
  seeking_height_max_feet?: number;
  seeking_height_max_inches?: number;
  seeking_financial_min?: number;
  seeking_islam_importance_min?: number;
  seeking_masjid_attendance_min?: string;
  seeking_beard_preference?: string;
  seeking_hijab_preference?: string;
  seeking_children_ok?: boolean;
  seeking_divorced_ok?: boolean;
  seeking_convert_ok?: boolean;
  seeking_preferences_weights?: any;
  matchmaker_notes?: string;
}

interface ProfileDetailModalProps {
  profileId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateMatch?: (profileId: string) => void;
  profile?: Profile | null;
  anonymized?: boolean;
  isAdmin?: boolean;
}

export function ProfileDetailModal({
  profileId,
  open,
  onOpenChange,
  onCreateMatch,
  profile: providedProfile,
  anonymized = false,
  isAdmin = false
}: ProfileDetailModalProps) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [matchStats, setMatchStats] = useState({
    totalMatches: 0,
    pending: 0,
    accepted: 0,
    rejected: 0
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch dealbreaker questions for this profile
  const { data: dealbreakerQuestions = [] } = useDealbreakerQuestions(profileId || undefined);

  useEffect(() => {
    if (!open) return;

    if (providedProfile) {
      setProfile(providedProfile as Profile);
      setLoading(false);
      if (!anonymized && profileId) {
        fetchMatchStats(profileId);
      }
      return;
    }

    if (!profileId) return;

    const fetchProfile = async () => {
      try {
        setLoading(true);

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", profileId)
          .single();

        if (profileError) throw profileError;

        setProfile(profileData as Profile);

        await fetchMatchStats(profileId);
      } catch (error) {
        console.error("profile.fetch_failed");
        toast.error("Failed to load profile details");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [profileId, open, providedProfile, anonymized]);

  const fetchMatchStats = async (id: string) => {
    try {
      const { data: matchesData } = await supabase
        .from("matches")
        .select("status, profile_1_id, profile_2_id, profile_1_response, profile_2_response")
        .or(`profile_1_id.eq.${id},profile_2_id.eq.${id}`);

      if (matchesData) {
        const totalMatches = matchesData.length;

        const pending = matchesData.filter(m => {
          if (m.profile_1_id === id) return !m.profile_1_response || m.profile_1_response === "pending";
          if (m.profile_2_id === id) return !m.profile_2_response || m.profile_2_response === "pending";
          return false;
        }).length;

        const accepted = matchesData.filter(m => {
          if (m.profile_1_id === id) return m.profile_1_response === "accepted";
          if (m.profile_2_id === id) return m.profile_2_response === "accepted";
          return false;
        }).length;

        const rejected = matchesData.filter(m => {
          if (m.profile_1_id === id) return m.profile_1_response === "rejected";
          if (m.profile_2_id === id) return m.profile_2_response === "rejected";
          return false;
        }).length;

        setMatchStats({
          totalMatches,
          pending,
          accepted,
          rejected
        });
      }
    } catch (error) {
      console.error("profile.match_stats_failed");
    }
  };

  const profilePicture = profile?.profile_picture_url || "";
  const galleryImages = profile?.profile_pictures && profile.profile_pictures.length > 0
    ? profile.profile_pictures
    : [];

  // Hooks must be called unconditionally — keep them above the early return.
  const signedProfilePicture = useSignedPhotoUrl(profilePicture);
  const signedGalleryImages = useSignedPhotoUrls(galleryImages);

  if (!open) return null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Complete Profile Details</DialogTitle>
          <DialogDescription>
            All profile information, preferences, and match history
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : profile ? (
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-6">
              {/* Header with Profile Picture Only */}
              <div className="flex items-start gap-4">
                <Avatar className="h-32 w-32">
                  {signedProfilePicture ? (
                    <AvatarImage
                      src={signedProfilePicture}
                      alt={profile.first_name}
                      className={anonymized ? "blur-md" : ""}
                    />
                  ) : null}
                  <AvatarFallback className="text-3xl">
                    {profile.first_name[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-2xl font-bold">{profile.first_name}, {profile.age}</h3>
                    {profile.is_active ? (
                      <Badge variant="default">Active</Badge>
                    ) : profile.profile_completed === false ? (
                      <Badge variant="outline">Profile incomplete</Badge>
                    ) : (
                      <Badge variant="secondary">Paused</Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground capitalize">
                    {profile.gender === "male" ? "Brother" : "Sister"} • {profile.location}
                  </p>
                  {!anonymized && (
                    <div className="flex gap-4 mt-2 text-sm">
                      <span className="text-muted-foreground">
                        Accepted: <span className="font-semibold text-green-600 dark:text-green-400">{matchStats.accepted}</span>
                      </span>
                      <span className="text-muted-foreground">
                        Rejected: <span className="font-semibold text-red-600 dark:text-red-400">{matchStats.rejected}</span>
                      </span>
                      <span className="text-muted-foreground">
                        Pending: <span className="font-semibold text-yellow-600 dark:text-yellow-400">{matchStats.pending}</span>
                      </span>
                    </div>
                  )}
                  {!anonymized && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => {
                        const matcherPath = isAdmin ? '/admin/smart-matcher' : '/matchmaker/smart-matcher';
                        navigate(`${matcherPath}?profileId=${profile.id}`);
                        onOpenChange(false);
                      }}
                    >
                      <UserPlus className="mr-2 h-4 w-4" />
                      Find Matches for {profile.first_name}
                    </Button>
                  )}
                </div>
              </div>

              {/* Photo Gallery */}
              {galleryImages.length > 0 && !anonymized && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-lg">Photos</h4>
                  <Card className="p-4">
                    <div className="relative w-full">
                      <div className="aspect-[4/3] w-full bg-muted rounded-lg overflow-hidden">
                        {signedGalleryImages?.[currentImageIndex] ? (
                          <img
                            src={signedGalleryImages[currentImageIndex] ?? undefined}
                            alt={`${profile.first_name} - Photo ${currentImageIndex + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : null}
                      </div>
                      {galleryImages.length > 1 && (
                        <>
                          <Button
                            variant="secondary"
                            size="icon"
                            className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full shadow-lg"
                            onClick={prevImage}
                          >
                            <ChevronLeft className="h-6 w-6" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full shadow-lg"
                            onClick={nextImage}
                          >
                            <ChevronRight className="h-6 w-6" />
                          </Button>
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium">
                            {currentImageIndex + 1} / {galleryImages.length}
                          </div>
                        </>
                      )}
                    </div>
                  </Card>
                </div>
              )}

              <Separator />

              {/* Basic Info */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Basic Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Ethnicity:</span>
                    <p className="font-medium">{profile.ethnicity}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Marital Status:</span>
                    <p className="font-medium capitalize">{profile.marital_status}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Citizenship:</span>
                    <p className="font-medium">{profile.citizenship_status}</p>
                  </div>
                  {profile.height_feet && (
                    <div>
                      <span className="text-muted-foreground">Height:</span>
                      <p className="font-medium">{profile.height_feet}'{profile.height_inches || 0}"</p>
                    </div>
                  )}
                  {profile.has_children !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Has Children:</span>
                      <p className="font-medium">
                        {profile.has_children ? `Yes (${profile.children_count || 0})` : "No"}
                      </p>
                    </div>
                  )}
                  {profile.willing_to_relocate !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Willing to Relocate:</span>
                      <p className="font-medium">{profile.willing_to_relocate ? "Yes" : "No"}</p>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Religious Profile */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Religious Profile</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Religiosity:</span>
                    <p className="font-medium capitalize">{profile.religiosity_level}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Prayer Frequency:</span>
                    <p className="font-medium">{profile.prayer_frequency}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Sect:</span>
                    <p className="font-medium capitalize">{profile.sect}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Masjid Attendance:</span>
                    <p className="font-medium">{profile.masjid_attendance}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Quran Knowledge:</span>
                    <p className="font-medium">{profile.quran_knowledge}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Islamic Knowledge:</span>
                    <p className="font-medium">{profile.islamic_knowledge_level}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Importance of Islam:</span>
                    <p className="font-medium">{profile.importance_of_islam}/10</p>
                  </div>
                  {profile.gender === "male" && profile.has_beard && (
                    <div>
                      <span className="text-muted-foreground">Has Beard:</span>
                      <p className="font-medium capitalize">{profile.has_beard}</p>
                    </div>
                  )}
                  {profile.gender === "female" && profile.wears_hijab !== undefined && (
                    <>
                      <div>
                        <span className="text-muted-foreground">Wears Hijab:</span>
                        <p className="font-medium">{profile.wears_hijab ? "Yes" : "No"}</p>
                      </div>
                      {profile.wears_hijab && profile.wears_hijab_level && (
                        <div>
                          <span className="text-muted-foreground">Hijab Level:</span>
                          <p className="font-medium capitalize">{profile.wears_hijab_level}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <Separator />

              {/* Education & Career */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Education & Career</h4>
                <div className="grid gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Education Level:</span>
                    <p className="font-medium">{profile.education_level}</p>
                  </div>
                  {profile.field_of_study && (
                    <div>
                      <span className="text-muted-foreground">Field of Study:</span>
                      <p className="font-medium">{profile.field_of_study}</p>
                    </div>
                  )}
                  {profile.employment_status && (
                    <div>
                      <span className="text-muted-foreground">Employment Status:</span>
                      <p className="font-medium capitalize">{profile.employment_status}</p>
                    </div>
                  )}
                  {profile.occupation && (
                    <div>
                      <span className="text-muted-foreground">Occupation:</span>
                      <p className="font-medium">{profile.occupation}</p>
                    </div>
                  )}
                  {profile.career_description && (
                    <div>
                      <span className="text-muted-foreground">Career Description:</span>
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                        {profile.career_description}
                      </p>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground">Financial Status:</span>
                    <p className="font-medium">{profile.financial_status}/10</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Lifestyle */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Lifestyle</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Smoking:</span>
                    <p className="font-medium capitalize">{profile.smoking}</p>
                  </div>
                  {profile.exercise_routine && (
                    <div>
                      <span className="text-muted-foreground">Exercise Routine:</span>
                      <p className="font-medium capitalize">{profile.exercise_routine}</p>
                    </div>
                  )}
                  {profile.dietary_preferences && profile.dietary_preferences.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Dietary Preferences:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {profile.dietary_preferences.map((pref) => (
                          <Badge key={pref} variant="outline" className="text-xs">
                            {pref}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Personality */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Personality</h4>
                <div className="grid gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Personality Type:</span>
                    <p className="font-medium">{profile.personality_type}/10 (1=Introverted, 10=Extroverted)</p>
                  </div>
                  {profile.personality_description && (
                    <div>
                      <span className="text-muted-foreground">Personality Description:</span>
                      <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
                        {profile.personality_description}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Willingness */}
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Openness</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {profile.willing_to_marry_divorced !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Open to Divorced:</span>
                      <p className="font-medium">{profile.willing_to_marry_divorced ? "Yes" : "No"}</p>
                    </div>
                  )}
                  {profile.willing_to_marry_with_children !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Open to Partner with Children:</span>
                      <p className="font-medium">{profile.willing_to_marry_with_children ? "Yes" : "No"}</p>
                    </div>
                  )}
                  {profile.willing_to_marry_convert !== undefined && (
                    <div>
                      <span className="text-muted-foreground">Open to Convert:</span>
                      <p className="font-medium">{profile.willing_to_marry_convert ? "Yes" : "No"}</p>
                    </div>
                  )}
                </div>
              </div>

              {profile.bio && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h4 className="font-semibold text-lg">About</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {profile.bio}
                    </p>
                  </div>
                </>
              )}

              <Separator />

              {/* Seeking Preferences - Priority Weights */}
              {profile.seeking_preferences_weights && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-lg">What Matters Most to Them</h4>
                  <PriorityBadges
                    weights={profile.seeking_preferences_weights}
                    firstName={profile.first_name}
                  />
                </div>
              )}

              <Separator />

              {/* What They're Looking For - Detailed */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg">What They're Looking For</h4>

                {/* Basic Criteria */}
                <Card className="p-4">
                  <h5 className="font-medium mb-3">Basic Criteria</h5>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Age Range:</span>
                      <p className="font-medium">{profile.seeking_age_min} - {profile.seeking_age_max}</p>
                    </div>
                    {profile.seeking_location_preference && (
                      <div>
                        <span className="text-muted-foreground">Location:</span>
                        <p className="font-medium">{profile.seeking_location_preference}</p>
                      </div>
                    )}
                    {profile.seeking_height_min_feet && (
                      <div>
                        <span className="text-muted-foreground">Height Range:</span>
                        <p className="font-medium">
                          {profile.seeking_height_min_feet}'{profile.seeking_height_min_inches || 0}" -
                          {profile.seeking_height_max_feet}'{profile.seeking_height_max_inches || 0}"
                        </p>
                      </div>
                    )}
                    {profile.seeking_personality_min !== undefined && (
                      <div>
                        <span className="text-muted-foreground">Personality Range:</span>
                        <p className="font-medium">
                          {profile.seeking_personality_min} - {profile.seeking_personality_max}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>

                {/* Educational & Professional */}
                {(profile.seeking_education && profile.seeking_education.length > 0) || profile.seeking_financial_min && (
                  <Card className="p-4">
                    <h5 className="font-medium mb-3">Educational & Professional</h5>
                    <div className="space-y-3 text-sm">
                      {profile.seeking_education && profile.seeking_education.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Education Levels:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {profile.seeking_education.map((edu) => (
                              <Badge key={edu} variant="outline" className="text-xs">
                                {edu}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {profile.seeking_financial_min && (
                        <div>
                          <span className="text-muted-foreground">Minimum Financial Status:</span>
                          <p className="font-medium">{profile.seeking_financial_min}/10</p>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Background */}
                {((profile.seeking_ethnicity && profile.seeking_ethnicity.length > 0) ||
                  (profile.seeking_citizenship && profile.seeking_citizenship.length > 0) ||
                  (profile.seeking_marital_status && profile.seeking_marital_status.length > 0)) && (
                  <Card className="p-4">
                    <h5 className="font-medium mb-3">Background</h5>
                    <div className="space-y-3 text-sm">
                      {profile.seeking_ethnicity && profile.seeking_ethnicity.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Ethnicity:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {profile.seeking_ethnicity.map((eth) => (
                              <Badge key={eth} variant="outline" className="text-xs">
                                {eth}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {profile.seeking_citizenship && profile.seeking_citizenship.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Citizenship:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {profile.seeking_citizenship.map((cit) => (
                              <Badge key={cit} variant="outline" className="text-xs">
                                {cit}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {profile.seeking_marital_status && profile.seeking_marital_status.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Marital Status:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {profile.seeking_marital_status.map((status) => (
                              <Badge key={status} variant="outline" className="text-xs capitalize">
                                {status}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Religious Criteria */}
                {((profile.seeking_religiosity && profile.seeking_religiosity.length > 0) ||
                  (profile.seeking_sect && profile.seeking_sect.length > 0) ||
                  profile.seeking_islam_importance_min ||
                  profile.seeking_masjid_attendance_min ||
                  profile.seeking_beard_preference ||
                  profile.seeking_hijab_preference) && (
                  <Card className="p-4">
                    <h5 className="font-medium mb-3">Religious Criteria</h5>
                    <div className="space-y-3 text-sm">
                      {profile.seeking_religiosity && profile.seeking_religiosity.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Religiosity Levels:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {profile.seeking_religiosity.map((rel) => (
                              <Badge key={rel} variant="outline" className="text-xs capitalize">
                                {rel}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {profile.seeking_sect && profile.seeking_sect.length > 0 && (
                        <div>
                          <span className="text-muted-foreground">Sects:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {profile.seeking_sect.map((sect) => (
                              <Badge key={sect} variant="outline" className="text-xs capitalize">
                                {sect}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                      {profile.seeking_islam_importance_min && (
                        <div>
                          <span className="text-muted-foreground">Min Importance of Islam:</span>
                          <p className="font-medium">{profile.seeking_islam_importance_min}/10</p>
                        </div>
                      )}
                      {profile.seeking_masjid_attendance_min && (
                        <div>
                          <span className="text-muted-foreground">Min Masjid Attendance:</span>
                          <p className="font-medium capitalize">{profile.seeking_masjid_attendance_min}</p>
                        </div>
                      )}
                      {profile.seeking_beard_preference && (
                        <div>
                          <span className="text-muted-foreground">Beard Preference:</span>
                          <p className="font-medium capitalize">{profile.seeking_beard_preference}</p>
                        </div>
                      )}
                      {profile.seeking_hijab_preference && (
                        <div>
                          <span className="text-muted-foreground">Hijab Preference:</span>
                          <p className="font-medium capitalize">{profile.seeking_hijab_preference}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {/* Openness Preferences */}
                {(profile.seeking_children_ok !== undefined ||
                  profile.seeking_divorced_ok !== undefined ||
                  profile.seeking_convert_ok !== undefined) && (
                  <Card className="p-4">
                    <h5 className="font-medium mb-3">Openness</h5>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {profile.seeking_children_ok !== undefined && (
                        <div>
                          <span className="text-muted-foreground">Open to Partner with Children:</span>
                          <p className="font-medium">{profile.seeking_children_ok ? "Yes" : "No"}</p>
                        </div>
                      )}
                      {profile.seeking_divorced_ok !== undefined && (
                        <div>
                          <span className="text-muted-foreground">Open to Divorced:</span>
                          <p className="font-medium">{profile.seeking_divorced_ok ? "Yes" : "No"}</p>
                        </div>
                      )}
                      {profile.seeking_convert_ok !== undefined && (
                        <div>
                          <span className="text-muted-foreground">Open to Convert:</span>
                          <p className="font-medium">{profile.seeking_convert_ok ? "Yes" : "No"}</p>
                        </div>
                      )}
                    </div>
                  </Card>
                )}

                {profile.seeking_description && (
                  <Card className="p-4">
                    <h5 className="font-medium mb-2">Additional Details</h5>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {profile.seeking_description}
                    </p>
                  </Card>
                )}

                {profile.matchmaker_notes && !anonymized && (
                  <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                    <h5 className="font-medium mb-2 text-blue-900 dark:text-blue-100">
                      Private Notes to Matchmaker
                    </h5>
                    <p className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
                      {isAdmin ? '[REDACTED - Content visible to matchmakers only]' : profile.matchmaker_notes}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 italic">
                      This information is private and only visible to matchmakers
                    </p>
                  </Card>
                )}

                {/* Dealbreaker Questions */}
                {dealbreakerQuestions.length > 0 && !anonymized && (
                  <Card className="p-4 bg-purple-50 dark:bg-purple-950/20 border-purple-200 dark:border-purple-800">
                    <h5 className="font-medium mb-2 text-purple-900 dark:text-purple-100">
                      Dealbreaker Questions
                    </h5>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mb-3 italic">
                      {isAdmin
                        ? 'Questions are redacted for admin privacy. Matchmakers can see these questions.'
                        : 'These questions will be shown to potential matches who must answer before accepting.'}
                    </p>
                    <div className="space-y-3">
                      {dealbreakerQuestions.map((question, index) => (
                        <div key={question.id} className="flex items-start gap-2">
                          <span className="font-semibold text-purple-700 dark:text-purple-300 text-sm">
                            Q{index + 1}:
                          </span>
                          <p className="text-sm text-purple-800 dark:text-purple-200 flex-1">
                            {isAdmin
                              ? '[REDACTED - Content visible to matchmakers only]'
                              : question.question_text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            </div>
          </ScrollArea>
        ) : (
          <p className="text-center text-muted-foreground py-8">Profile not found</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
